"use client";

import React, { useState } from "react";
import {
  Briefcase01Icon,
  Clock04Icon,
  Location04Icon,
  JobLinkIcon,
  ChartIncreaseIcon,
  DashboardCircleIcon,
  Mortarboard01Icon,
} from "hugeicons-react";
import { useClientTranslations } from "@/lib/translate";
import { Job } from "@/modules/types/job/job";
import { diffDate } from "@/lib/utils/formatter";
import moment from "moment";
import { Category, Degree, Level } from "@/modules/types/metadata/metadata";
import DisplaySkills from "@/modules/components/commons/DisplaySkills";
import { JobSalaryDisplay } from "./JobSalaryDisplay";
import ListImagesPopupView from "@/modules/components/commons/ListImagesPopupView";
import JobApplyStatusBadge from "./JobApplyStatusBadge";
import AIStar from "../commons/icon/AIStar";
import { useAIGenSuggestionForJob } from "@/modules/hooks/employee/useEmployeeInfo";
import Markdown from "react-markdown";
import { useApplyJob } from "@/modules/hooks/apply/useApplyJob";
import ApplyCallLoginPopup from "./ApplyCallLoginPopup";
import AICallLoginPopup from "./AICallLoginPopup";
import CircularProgress from "../commons/CircularProgress";
import ApplyJobModal from "./ApplyJobModal";
import { SaveJobButton } from "./SaveJobButton";

export const JobDetails = ({
  job,
  levels,
  categories,
  degrees,
}: {
  job: Job;
  levels: Level[];
  categories: Category[];
  degrees: Degree[];
}) => {
  const { t } = useClientTranslations("job-info");
  const { t: tMeta } = useClientTranslations("metadata");

  // Tabs state
  const [activeTab, setActiveTab] = useState("details");
  const {
    genSuggestion,
    loading,
    suggestion,
    openLoginModal,
    onCloseLoginModal,
  } = useAIGenSuggestionForJob(job.id);
  const {
    loading: applyLoading,
    openLoginModal: applyOpenLoginModal,
    onCloseLoginModal: onCloseApplyLoginModal,
    openApplyJobModal,
    setOpenApplyJobModal,
    applyJob,
    onApplyJob,
    applyStatus,
  } = useApplyJob(job.id);

  // Handler for AI suggestion button
  const handleGenerateSuggestion = async () => {
    await genSuggestion();
  };

  const yearExperienceFormat = (minYear: number, maxYear: number) => {
    if (maxYear <= 0) {
      if (minYear > 0) {
        return `${t("atLeast")} ${minYear} ${minYear > 1 ? t("years") : t("year")
          }`;
      } else {
        return t("noExperience");
      }
    }
    if (minYear === maxYear) {
      return `${minYear} ${minYear > 1 ? t("years") : t("year")}`;
    }
    if (minYear <= 0) {
      if (maxYear === Infinity) {
        return t("anyExperience");
      }
      return `${t("atLeast")} ${maxYear} ${maxYear > 1 ? t("years") : t("year")
        }`;
    }
    return `${minYear} - ${maxYear} ${t("years")}`;
  };

  const jobApplyStatusComponent = () => {
    if (!job.jobApplyStatus && !applyStatus) return null;
    return (
      <JobApplyStatusBadge
        forceUpdate
        jobApplyId={job.jobApplyId || ""}
        status={job.jobApplyStatus || applyStatus!}
      />
    );
  };

  return (
    <div className="h-fit">
      <div className="sm:sticky top-[70px] z-10 bg-white sm:rounded-lg p-6 sm:shadow-sm mb-6">
        <div className="flex items-start gap-4">
          <div className="flex-1">
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-gray-900 mb-3">
              {job.jobTitle}
            </h1>
            {job.companyInfo && (
              <p className="text-gray-600 mb-3">
                <span className="hidden">{t("fromCompany")}</span>
                {job.companyInfo.companyName}
              </p>
            )}
            <div className="flex items-center text-gray-600 mb-3">
              <Location04Icon className="min-w-5 min-h-5 flex-shrink-0 mr-2" />
              <a
                href={`https://maps.google.com/?q=${job.address}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primaryDark underline transition-colors duration-300"
              >
                <span>{job.address}</span>
              </a>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-5">
              <div className="flex items-center text-gray-600">
                <Briefcase01Icon className="min-w-5 min-h-5 flex-shrink-0 mr-2" />
                <span>{tMeta(`jobType.${job.jobType}`)}</span>
              </div>
              <div className="flex items-center text-gray-600">
                <Clock04Icon className="min-w-5 min-h-5 flex-shrink-0 mr-2" />
                {job.createdAt && (
                  <span>{diffDate(job.createdAt, moment().unix())}</span>
                )}
              </div>
              <div className="text-primary font-semibold">
                <JobSalaryDisplay
                  requiredLogin={job.requiredLogin}
                  isNegotiable={job.isNegotiable}
                  minSalary={job.minSalary}
                  maxSalary={job.maxSalary}
                  moneyUnit={job.moneyUnit}
                  loginToViewSalary={t("loginToViewSalary")}
                  salaryNegotiable={t("salaryNegotiable")}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full flex gap-3 items-center mb-4">
          <ApplyCallLoginPopup
            applyOpenLoginModal={applyOpenLoginModal}
            onCloseApplyLoginModal={onCloseApplyLoginModal}
          />
          {job.isApplied || applyStatus ? (
            jobApplyStatusComponent()
          ) : (
            <button
              onClick={applyJob}
              className="bg-primary text-white py-3 max-h-fit rounded-lg hover:bg-primaryDark transition-colors duration-300 w-full"
            >
              {applyLoading ? <CircularProgress /> : t("applyNow")}
            </button>
          )}
          <SaveJobButton
            jobId={job.id}
            isSaved={job.isSaved}
            disabled={job.isApplied}
            showText
          />
        </div>
      </div>

      <div id="job-info" className="lg:col-span-2 space-y-6">
        <div className="bg-white p-0 sm:rounded-lg shadow-none sm:shadow-none space-y-4">
          {/* Tabs Header */}
          <div className="flex justify-center space-x-4 mb-6">
            <button
              className={`relative px-6 py-2 font-semibold transition-all duration-200 ${activeTab === "details"
                ? "text-primary"
                : "text-gray-500 hover:text-primary"
                }`}
              onClick={() => setActiveTab("details")}
            >
              {t("jobDetails")}
              {activeTab === "details" && (
                <span className="absolute left-0 right-0 -bottom-1 h-1 bg-primary rounded-full"></span>
              )}
            </button>
            <button
              className={`relative px-6 py-2 font-semibold transition-all duration-200 ${activeTab === "ai"
                ? "text-primary"
                : "text-gray-500 hover:text-primary"
                }`}
              onClick={() => setActiveTab("ai")}
            >
              <AIStar className="mr-2" />
              {t("aiGenSuggestion")}
              {activeTab === "ai" && (
                <span className="absolute left-0 right-0 -bottom-1 h-1 bg-primary rounded-full"></span>
              )}
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === "details" && (
            <div className="p-6 sm:shadow-sm space-y-4">
              <div className="w-full">
                {job.companyInfo?.companyImages && (
                  <ListImagesPopupView
                    images={job.companyInfo?.companyImages}
                  />
                )}
              </div>
              <p className="text-gray-700">
                <span className="font-semibold">{t("skills")}: </span>
              </p>
              <div className="w-full list-inside">
                <DisplaySkills skills={job.skills} />
              </div>
              <p className="flex items-center justify-start gap-1 text-gray-700">
                <JobLinkIcon className="size-5" />
                <span className="font-semibold">{t("experience")}: </span>
                {yearExperienceFormat(
                  job.minYearExperience,
                  job.maxYearExperience
                )}
              </p>
              <p className="flex items-center justify-start gap-1 text-gray-700">
                <ChartIncreaseIcon className="size-5" />
                <span className="font-semibold">{t("level")}: </span>
                {levels.find((level) => level.id === job.levelId)?.title}
              </p>
              <p className="flex items-center justify-start gap-1 text-gray-700">
                <DashboardCircleIcon className="size-5" />
                <span className="font-semibold">{t("category")}: </span>
                {
                  categories.find((category) => category.id === job.categoryId)
                    ?.title
                }
              </p>
              <p className="flex items-center justify-start gap-1 text-gray-700">
                <Mortarboard01Icon className="size-5" />
                <span className="font-semibold">{t("educate")}: </span>
                {degrees.find((degree) => degree.id === job.degreeId)?.title}
              </p>

              <div className="w-full border-t border-gray-300 border-dashed mt-5" />
              <h3 className="text-xl font-semibold text-gray-700">
                {t("jobDescription")}
              </h3>
              <div
                className="text-gray-600 w-full list-inside"
                dangerouslySetInnerHTML={{ __html: job.jobDescription }}
              ></div>

              <h3 className="text-xl font-semibold text-gray-700">
                {t("requirements")}
              </h3>
              <div
                className="text-gray-600 w-full list-inside"
                dangerouslySetInnerHTML={{ __html: job.jobRequirement }}
              ></div>

              {job.jobResponsibility && job.jobResponsibility.length > 0 && (
                <>
                  <h3 className="text-xl font-semibold text-gray-700">
                    {t("responsibilities")}
                  </h3>
                  <div
                    className="text-gray-600 w-full list-inside"
                    dangerouslySetInnerHTML={{ __html: job.jobResponsibility }}
                  ></div>
                </>
              )}

              <h3 className="text-xl font-semibold text-gray-700">
                {t("benefits")}
              </h3>
              <div
                className="text-gray-600 w-full list-inside"
                dangerouslySetInnerHTML={{ __html: job.jobBenefit }}
              ></div>

              {job.jobNote && job.jobNote.length > 0 && (
                <>
                  <h3 className="text-xl font-semibold text-gray-700">
                    {t("note")}
                  </h3>
                  <div
                    className="text-gray-600 w-full list-inside"
                    dangerouslySetInnerHTML={{ __html: job.jobNote }}
                  ></div>
                </>
              )}
            </div>
          )}
          {activeTab === "ai" && (
            <div className="">
              <AICallLoginPopup
                openLoginModal={openLoginModal}
                onCloseLoginModal={onCloseLoginModal}
              />
              {suggestion && suggestion.length > 0 ? (
                <div className="w-full p-6 text-gray-700">
                  <Markdown>{suggestion}</Markdown>
                </div>
              ) : (
                <div className="w-full flex flex-col items-center justify-center min-h-[300px]">
                  <button
                    className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-primaryDark transition-colors duration-300 mb-6 disabled:opacity-60"
                    onClick={handleGenerateSuggestion}
                    disabled={loading}
                  >
                    <AIStar className="mr-2" />
                    {loading ? t("genAiButtonLoading") : t("genAiButton")}
                  </button>
                  <div className="w-full text-center text-gray-700 min-h-[60px]">
                    {loading ? (
                      <div className="w-full p-6 flex flex-col gap-2">
                        <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[70%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[80%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[70%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[80%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[70%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                        <div className="w-[90%] h-[15px] bg-gray-200 rounded-full animate-pulse" />
                      </div>
                    ) : (
                      <p>{t("genAiButtonDisabled")}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      <ApplyJobModal
        open={openApplyJobModal}
        onClose={() => setOpenApplyJobModal(false)}
        onSubmit={onApplyJob}
        loading={applyLoading}
      />
    </div>
  );
};
